import React from 'react'
import { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Container, Grid } from '@material-ui/core'

import { fetchProduct, fetchProductIds, fetchToppings } from '@api/client'
import { HomepageCartButton } from '@components/cart'
import { ProgressBar, SEO, SPCard } from '@components/common'
import { BaseProduct, ProductDetails } from '@components/product'
import { ProductsCardSkeleton } from '@components/skeleton'
import { useCart } from '@context'
import { useSimillarProducts } from '@hooks'
import { Product, Topping } from '@interfaces/restaurant'
import { CONFIG } from '@utils/config'
import { formattedProductLink } from '@utils/helpers'

interface Props {
    product: Product | null
    toppings: Topping[]
}

const ProductPage: NextPage<Props> = ({ product, toppings }) => {
    const router = useRouter()
    const cart = useCart()

    const { simillarProducts, loadingSimillarProducts } =
        useSimillarProducts(product)

    if (router.isFallback) {
        return (
            <div className="container center">
                <ProgressBar />
            </div>
        )
    }

    return (
        <>
            <SEO
                title={`${product.name} | ${CONFIG.RESTAURANT_NAME}`}
                description={product.description}
                imageUrl={product.file_path}
            />

            <Container className="container pv2">
                <Grid container spacing={2}>
                    <Grid item xs={12} md={7} className="white border radius">
                        <ProductDetails
                            product={product}
                            toppings={toppings}
                            isDialog={false}
                            onAddToCartClick={() => {
                                cart.setOpen(true)
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={5}>
                        {loadingSimillarProducts ? (
                            <ProductsCardSkeleton count={6} />
                        ) : (
                            <div className="h2-margin">
                                <SPCard title="Similare">
                                    {simillarProducts?.map((p) => (
                                        <div key={p.id} className="mt05 separator">
                                            <Link
                                                href={`/${formattedProductLink(p)}`}
                                                passHref
                                            >
                                                <a>
                                                    <BaseProduct product={p} />
                                                </a>
                                            </Link>
                                        </div>
                                    ))}
                                </SPCard>
                            </div>
                        )}
                    </Grid>
                </Grid>
            </Container>
            <HomepageCartButton />
        </>
    )
}

export default ProductPage

export const getStaticPaths: GetStaticPaths = async () => {
    const productIds = await fetchProductIds()

    return {
        paths: productIds.map((id) => ({ params: { id } })),
        fallback: true,
    }
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
    try {
        const [product, toppings] = await Promise.all([
            fetchProduct(`${params.id}`),
            fetchToppings(),
        ])

        if (!product) {
            return {
                notFound: true,
            }
        }

        return {
            props: { product, toppings },
            revalidate: 60,
        }
    } catch (error) {
        return {
            notFound: true,
        }
    }
}
